import React, { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import auth from '../utils/auth';
import localCart from '../utils/localCartStorage';

const CARD_OPTIONS = {
    iconStyle: 'solid',
    style: {
        base: {
            iconColor: "#555",
            color: '#555',
            fontWeight: 500,
            fontFamily: "Roboto, Open Sans, Segoe UI, sans-serif",
            fontSize: "16px",
            fontSmoothing: 'antialiased',
            ":-webkit-autofill": { color: "#fce883" },
            "::placeholder": { color: "#555" },
        },
        invalid: {
            iconColor: '#dc3545',
            color: '#dc3545'
        }
    }
};

const PaymentForm = ({ onPaymentSuccess, isLoading, guestEmail }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [error, setError] = useState(null);
    const [processing, setProcessing] = useState(false);
    const isAuthenticated = auth.isAuthenticated();

    const handleCardChange = (event) => {
        if (event.error) {
            setError(event.error.message);
        } else {
            setError('');
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError(null);

        if (!stripe || !elements) {
            return;
        }

        setProcessing(true);

        const cardElement = elements.getElement(CardElement);

        try {
            const { error, paymentMethod } = await stripe.createPaymentMethod({
                type: 'card',
                card: cardElement,
            });

            if (error) {
                setError(`Betalningsfel: ${error.message}`);
                setProcessing(false);
                return;
            }

            const paymentEndpoint = isAuthenticated
                ? `${process.env.REACT_APP_API_BASE_URL}/orders/payment`
                : `${process.env.REACT_APP_API_BASE_URL}/orders/guest-payment`;

            let requestBody = {
                id: paymentMethod.id
            };

            if (!isAuthenticated) {
                if (!guestEmail) {
                    setError('E-postadress krävs för gästbetalning');
                    setProcessing(false);
                    return;
                }

                const cart = localCart.getCart();
                requestBody = {
                    ...requestBody,
                    email: guestEmail,
                    items: cart.items
                };
            }

            const response = await fetch(paymentEndpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(isAuthenticated && { 'Authorization': `Bearer ${auth.getToken()}` })
                },
                body: JSON.stringify(requestBody)
            });

            const paymentData = await response.json();

            if (!response.ok) {
                throw new Error(paymentData.message || 'Ett fel uppstod vid betalningen');
            }

            if (paymentData.status === 'succeeded') {
                onPaymentSuccess(paymentData.paymentIntentId, paymentData.orderId);
            } else {
                setError(`Betalningen misslyckades. Status: ${paymentData.status}`);
            }
        } catch (error) {
            setError(error.message || 'Ett fel uppstod vid betalningen');
        } finally {
            setProcessing(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="payment-form">
            <div className="card-element-container">
                <CardElement options={CARD_OPTIONS} onChange={handleCardChange} />
            </div>
            {error && <div className="payment-error">{error}</div>}
            <button
                type="submit"
                disabled={!stripe || processing || isLoading}
                className="payment-button"
            >
                {processing ? 'Bearbetar...' : 'Betala nu'}
            </button>
        </form>
    );
};

export default PaymentForm;